import { useEffect, useState } from 'react';
import { MapPin, Calendar, Check, X, Loader2 } from 'lucide-react';
import { insertChatMessage } from '@/queries/chat';
import { fetchMeetupRequestById, respondToMeetupRequest } from '@/queries/offers';
import { toast } from '@/lib/toast';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';

interface InlineMeetupCardProps {
	mine: boolean;
	conversationId: string;
	metadata: any;
	currentUserId: string | undefined;
}

/**
 * Inline local-pickup card. Reads the live `meetup_requests` row so status
 * stays correct across realtime updates.
 */
export const InlineMeetupCard = ({
	mine,
	conversationId,
	metadata,
	currentUserId,
}: InlineMeetupCardProps) => {
	const meetupId = metadata?.meetup_request_id as string | undefined;
	const [meetup, setMeetup] = useState<any>(null);
	const [busy, setBusy] = useState(false);

	useEffect(() => {
		if (!meetupId) return;
		let cancelled = false;
		(async () => {
			const data = await fetchMeetupRequestById(meetupId);
			if (!cancelled) setMeetup(data);
		})();
		return () => {
			cancelled = true;
		};
	}, [meetupId]);

	const status = meetup?.status ?? 'pending';
	const proposedAt = meetup?.proposed_at ? new Date(meetup.proposed_at) : null;
	const canAct = !mine && currentUserId && status === 'pending';

	const respond = async (next: 'confirmed' | 'declined') => {
		if (!meetupId) return;
		setBusy(true);
		try {
			const { error } = await respondToMeetupRequest(meetupId, next);
			if (error) throw error;
			setMeetup((m: any) => ({ ...m, status: next }));
			await insertChatMessage({
				conversationId,
				senderId: currentUserId!,
				kind: 'meetup',
				body: next === 'confirmed' ? 'Confirmed pickup' : 'Declined pickup',
				metadata: { ...metadata, status: next },
			});
			toast.success(next === 'confirmed' ? 'Pickup confirmed' : 'Pickup declined');
		} catch (err: any) {
			toast.error('Could not update', { description: err?.message });
		} finally {
			setBusy(false);
		}
	};

	const statusBadge =
		status === 'confirmed' ? (
			<span className="inline-flex items-center gap-1 text-[10.5px] font-medium text-success bg-success/15 rounded-full px-2 py-0.5">
				<Check className="h-3 w-3" />
				Confirmed
			</span>
		) : status === 'declined' ? (
			<span className="inline-flex items-center gap-1 text-[10.5px] font-medium text-destructive bg-destructive/15 rounded-full px-2 py-0.5">
				Declined
			</span>
		) : (
			<span className="inline-flex items-center gap-1 text-[10.5px] font-medium text-[hsl(var(--active-gold))] bg-[hsl(var(--active-gold)/0.12)] rounded-full px-2 py-0.5">
				Pending
			</span>
		);

	return (
		<div
			className={cn(
				'max-w-[82%] rounded-2xl px-3.5 py-3 space-y-2',
				mine
					? 'bg-primary/12 border border-primary/30 rounded-br-md'
					: 'bg-white/[0.05] border border-white/[0.06] rounded-bl-md'
			)}
		>
			<div className="flex items-center justify-between gap-2">
				<div className="text-[11.5px] text-muted-foreground font-medium uppercase tracking-wide">
					Local pickup
				</div>
				{statusBadge}
			</div>
			{meetup?.proposed_location && (
				<div className="flex items-start gap-2 text-[13px] text-foreground">
					<MapPin className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
					<span className="leading-snug">{meetup.proposed_location}</span>
				</div>
			)}
			{proposedAt && (
				<div className="flex items-center gap-2 text-[13px] text-foreground">
					<Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
					<span>{format(proposedAt, 'EEE, MMM d · h:mm a')}</span>
				</div>
			)}
			{meetup?.notes && (
				<div className="text-[12px] text-muted-foreground italic">{meetup.notes}</div>
			)}
			{canAct && (
				<div className="flex gap-1.5 pt-1">
					<button
						type="button"
						onClick={() => respond('confirmed')}
						disabled={busy}
						className="h-8 px-3 rounded-full text-[12px] font-medium bg-[hsl(var(--active-gold))] text-black hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1"
					>
						{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
						Confirm
					</button>
					<button
						type="button"
						onClick={() => respond('declined')}
						disabled={busy}
						className="h-8 px-3 rounded-full text-[12px] font-medium bg-transparent text-muted-foreground hover:text-foreground hover:bg-white/[0.04] inline-flex items-center gap-1"
					>
						<X className="h-3 w-3" />
						Decline
					</button>
				</div>
			)}
		</div>
	);
};

export default InlineMeetupCard;
